import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ImagePlus, Video, X, Loader2, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import api from '../services/api';
import { toast } from 'sonner';

const CATEGORIES = ['Electronics', 'Furniture', 'Clothing', 'Home & Garden', 'Sports', 'Toys', 'Vehicles', 'Other'];

export default function MarketplaceNew() {
  const nav = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('Other');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [photos, setPhotos] = useState([]); // {url, rel_path}
  const [video, setVideo] = useState(null); // {url}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('prefix', 'marketplace');
    const r = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const d = await upload(f);
        setPhotos((p) => [...p, { url: d.url, rel_path: d.rel_path }]);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Photo upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const onVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error('Video too large — max 50 MB'); return; }
    setUploading(true);
    try {
      const d = await upload(f);
      setVideo({ url: d.url });
      toast.success('Video uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Video upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const submit = async () => {
    if (!title.trim() || title.trim().length < 3) return toast.error('Add a title (3+ characters)');
    if (!description.trim() || description.trim().length < 10) return toast.error('Describe your item (10+ characters)');
    if (!price || Number(price) <= 0) return toast.error('Set a price');
    if (!photos.length) return toast.error('Add at least one photo');
    if (lat == null || lng == null) return toast.error('Pick your item location from the address suggestions');
    setSubmitting(true);
    try {
      await api.post('/marketplace/listings', {
        title: title.trim(), description: description.trim(), price: Number(price), category,
        address, lat, lng,
        photos: photos.map((p) => p.url),
        photo_rel_paths: photos.map((p) => p.rel_path),
        video: video?.url || null,
      });
      toast.success('Listing submitted! Our AI is checking it and a ShipitNow admin will approve it shortly.');
      nav('/marketplace/mine');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not create listing'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => nav('/marketplace')} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Marketplace
        </button>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Sell an item</h1>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4 text-emerald-600" /> Every listing is AI-screened for fakes and approved by ShipitNow before going live.
        </p>

        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <div>
            <label className="text-sm text-slate-700">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 h-10" placeholder="e.g. IKEA desk — like new" data-testid="listing-title" />
          </div>
          <div>
            <label className="text-sm text-slate-700">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1.5" rows={4}
              placeholder="Condition, age, dimensions, why you're selling…" data-testid="listing-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-700">Price</label>
              <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5 h-10" placeholder="0.00" data-testid="listing-price" />
              <p className="text-[11px] text-slate-400 mt-1">Currency follows your city (Ottawa = CAD, Ibadan = NGN).</p>
            </div>
            <div>
              <label className="text-sm text-slate-700">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="listing-category">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-700">Item location (driver pickup point)</label>
            <AddressAutocomplete value={address}
              onChange={(addr, geo) => { setAddress(addr); setLat(geo?.lat ?? null); setLng(geo?.lng ?? null); }}
              placeholder="Where is the item? (Ottawa or Ibadan)" className="mt-1.5" />
          </div>

          <div>
            <label className="text-sm text-slate-700">Photos (up to 5)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative h-20 w-20">
                  <img src={p.url} alt="" className="h-20 w-20 rounded-lg object-cover border border-slate-200" />
                  <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5" data-testid={`remove-photo-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-emerald-400 hover:text-emerald-500" data-testid="add-photo-label">
                  <ImagePlus className="h-5 w-5" />
                  <span className="text-[10px] mt-0.5">Add</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} data-testid="listing-photo-input" />
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-700">Video (optional, max 50 MB)</label>
            {video ? (
              <div className="mt-2 flex items-center gap-3">
                <video src={video.url} className="h-24 rounded-lg border border-slate-200" controls />
                <button onClick={() => setVideo(null)} className="text-rose-500 text-sm" data-testid="remove-video-btn">Remove</button>
              </div>
            ) : (
              <label className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 cursor-pointer hover:border-emerald-400" data-testid="add-video-label">
                <Video className="h-4 w-4" /> Upload video
                <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={onVideo} data-testid="listing-video-input" />
              </label>
            )}
          </div>

          <Button onClick={submit} disabled={submitting || uploading} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-listing-btn">
            {(submitting || uploading) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {uploading ? 'Uploading…' : submitting ? 'Submitting…' : 'Submit for approval'}
          </Button>
        </div>
      </main>
    </div>
  );
}
